import {
  AI_MARKERS,
  applyAiMarker,
  getDisclaimer,
  parseAiAlt,
  type AiMarkerKind,
} from "@repo/ai-disclaimer";

/** Every valid marker kind, derived from the core so the two cannot drift apart. */
export const AI_MARKER_KINDS: readonly AiMarkerKind[] = AI_MARKERS.map(
  (entry) => entry.kind,
);

/**
 * Total type guard for a marker kind, for validating untrusted input.
 *
 * `applyAiMarker` **throws** on an unrecognised kind (`alt.ts`'s `markerFor`), so an
 * unvalidated value from a request body reaches a throwing call. A bulk handler that lets
 * that throw escape its per-item loop abandons the report of the writes it already made —
 * and those writes are irreversible. So callers validate at the boundary with this, and
 * additionally keep the per-item computation inside their own try.
 */
export function isAiMarkerKind(value: unknown): value is AiMarkerKind {
  return (
    typeof value === "string" &&
    (AI_MARKER_KINDS as readonly string[]).includes(value)
  );
}

/**
 * The subset of a Storyblok Management API asset record this module needs.
 *
 * Deliberately not the full record: the endpoint returns ~24 fields, and naming only what
 * is read keeps the CMS coupling visible and the fixtures small. `alt` and `content_type`
 * are optional because the live library has assets that carry neither.
 */
export type InventoryAsset = {
  id: number;
  filename?: string | null;
  /**
   * The bare name, e.g. `hero.png`.
   *
   * Preferred for display: `filename` is the full CDN URL, and a table of 632 of those is
   * unreadable — the editor needs to recognise an asset, not read its host.
   */
  short_filename?: string | null;
  alt?: string | null;
  content_type?: string | null;
};

export type InventoryRow = {
  id: number;
  filename: string;
  /**
   * The full CDN url, for the preview thumbnail.
   *
   * Kept separate from `filename`, which is the short display name: recognising an asset
   * from `hero.png` alone is unreliable across 632 of them, so the editor gets the picture.
   * Empty when the asset record carries no `filename` — the UI must handle that.
   */
  url: string;
  /** The raw stored value, marker included — what a write must round-trip. */
  rawAlt: string;
  /** What a reader should see. */
  cleanAlt: string;
  isAi: boolean;
  kind: AiMarkerKind | null;
  /**
   * True for a video asset.
   *
   * Carried on the row rather than re-derived in the UI because the two things that
   * differ for a video — no image-service thumbnail, and no background measurement —
   * are decided in different places, and both have to agree.
   */
  isVideo: boolean;
};

export type InventoryPartition = {
  images: InventoryRow[];
  /**
   * Video assets, which carry a badge exactly as images do.
   *
   * A bucket of their own rather than folded into `images`: they are markable and they
   * belong in the registry, but they have no image-service thumbnail and no measurable
   * background, so the UI and the badge both need to tell them apart.
   */
  videos: InventoryRow[];
  /** PDFs, archives — anything a badge could never render over. */
  nonImages: InventoryRow[];
  /**
   * Assets whose `content_type` is absent or empty.
   *
   * A separate bucket rather than a silent drop or a guess: the count is surfaced in the UI
   * so an asset can never vanish from the inventory unaccounted for. `images + nonImages +
   * unknown` always equals the input length, which is what makes the live reconciliation
   * check meaningful.
   */
  unknown: InventoryRow[];
};

export type InventoryCounts = {
  total: number;
  images: number;
  videos: number;
  nonImages: number;
  unknown: number;
  marked: number;
  unmarked: number;
  byKind: Record<AiMarkerKind, number>;
};

export type BulkAction = "apply" | "remove";

export type BulkOutcome =
  | { id: number; ok: true; alt: string }
  | { id: number; ok: false; reason: string };

export type BulkResult = {
  succeeded: { id: number; alt: string }[];
  failed: { id: number; reason: string }[];
};

const IMAGE_CONTENT_TYPE = /^image\//i;
const VIDEO_CONTENT_TYPE = /^video\//i;

/**
 * Whether an asset is one a badge could ever render over.
 *
 * Exported because two places have to agree on it: `partitionAssets`, which decides what
 * the `marked` count counts, and the registry rebuild, which decides what the badge
 * resolves from. When those two disagree the config page's drift warning fires on a
 * library that is perfectly in step — and a warning that cries wolf is worse than none,
 * because the real drift it exists to catch is a badge silently missing from a page.
 *
 * An absent or blank `content_type` is NOT an image. It is genuinely unknown, and the
 * inventory keeps it in its own bucket rather than guessing.
 */
export function isInventoryImage(
  contentType: string | null | undefined,
): boolean {
  const trimmed = contentType?.trim();

  return !!trimmed && IMAGE_CONTENT_TYPE.test(trimmed);
}

/** Whether an asset is a video, which carries a badge exactly as an image does. */
export function isInventoryVideo(
  contentType: string | null | undefined,
): boolean {
  const trimmed = contentType?.trim();

  return !!trimmed && VIDEO_CONTENT_TYPE.test(trimmed);
}

/**
 * Whether a badge could ever render over this asset.
 *
 * The single predicate the inventory counts and the registry rebuild both use — see
 * `isInventoryImage` for why those two must not be allowed to disagree. Videos were
 * added here rather than to `isInventoryImage` so nothing that genuinely means "is an
 * image" (a thumbnail url, a background measurement) silently starts claiming a video.
 */
export function isMarkableAsset(
  contentType: string | null | undefined,
): boolean {
  return isInventoryImage(contentType) || isInventoryVideo(contentType);
}

function toRow(asset: InventoryAsset): InventoryRow {
  const parsed = parseAiAlt(asset.alt);

  return {
    isVideo: isInventoryVideo(asset.content_type),
    id: asset.id,
    filename: asset.short_filename?.trim() || asset.filename || "",
    url: asset.filename ?? "",
    rawAlt: asset.alt ?? "",
    cleanAlt: parsed.cleanAlt,
    isAi: parsed.isAi,
    kind: parsed.kind,
  };
}

/**
 * Splits assets into images, videos, non-markable assets, and those with no declared
 * content type.
 *
 * The remaining non-markable assets — PDFs, archives — are partitioned out rather than
 * filtered silently: listing them as "unmarked assets needing a disclaimer" would be
 * actively misleading, and dropping them would make the counts stop adding up.
 */
export function partitionAssets(assets: InventoryAsset[]): InventoryPartition {
  const partition: InventoryPartition = {
    images: [],
    videos: [],
    nonImages: [],
    unknown: [],
  };

  for (const asset of assets) {
    const row = toRow(asset);
    const contentType = asset.content_type?.trim();

    if (!contentType) {
      partition.unknown.push(row);
    } else if (isInventoryImage(contentType)) {
      partition.images.push(row);
    } else if (isInventoryVideo(contentType)) {
      partition.videos.push(row);
    } else {
      partition.nonImages.push(row);
    }
  }

  return partition;
}

/**
 * Aggregate counts for the inventory header.
 *
 * Marked/unmarked span everything a badge can render over — images and videos — so they
 * stay comparable with the registry size the config page checks them against.
 */
export function countInventory(partition: InventoryPartition): InventoryCounts {
  const byKind: Record<AiMarkerKind, number> = {
    generated: 0,
    modified: 0,
    unspecified: 0,
  };

  let marked = 0;

  for (const row of [...partition.images, ...partition.videos]) {
    if (row.isAi && row.kind) {
      marked += 1;
      byKind[row.kind] += 1;
    }
  }

  const images = partition.images.length;
  const videos = partition.videos.length;
  const nonImages = partition.nonImages.length;
  const unknown = partition.unknown.length;

  return {
    total: images + videos + nonImages + unknown,
    images,
    videos,
    nonImages,
    unknown,
    marked,
    unmarked: images + videos - marked,
    byKind,
  };
}

/**
 * The new `alt` for a bulk action, or `null` when the action would change nothing.
 *
 * Returning `null` for a no-op is what keeps a bulk run from issuing hundreds of writes
 * that rewrite values to themselves. Against a library where only 1 of 703 assets is
 * currently marked, a "remove from everything" run would otherwise be ~700 pointless
 * requests against a rate-limited third-party API.
 */
export function computeAlt(
  rawAlt: string,
  action: BulkAction,
  kind: AiMarkerKind = "unspecified",
): string | null {
  const next =
    action === "apply"
      ? applyAiMarker(rawAlt, kind)
      : parseAiAlt(rawAlt).cleanAlt;

  return next === rawAlt ? null : next;
}

/**
 * Folds per-asset outcomes into a report.
 *
 * A bulk run is **not** atomic — the Management API takes one asset per request, so a
 * failure partway through leaves earlier assets already written. Reporting per-id is
 * therefore the only honest shape: "it failed" would hide the writes that did land, and an
 * exception would discard them entirely.
 */
export function reduceBulkResult(outcomes: BulkOutcome[]): BulkResult {
  const result: BulkResult = { succeeded: [], failed: [] };

  for (const outcome of outcomes) {
    if (outcome.ok) {
      result.succeeded.push({ id: outcome.id, alt: outcome.alt });
    } else {
      result.failed.push({ id: outcome.id, reason: outcome.reason });
    }
  }

  return result;
}

/**
 * 207 when anything failed, 200 otherwise.
 *
 * Split out as a pure function so the status contract is unit-testable — `apps/web` has no
 * test runner, and a 200 that quietly carries a non-empty `failed[]` is exactly the bug
 * that would otherwise ship unnoticed.
 */
export function statusForBulkResult(result: BulkResult): 200 | 207 {
  return result.failed.length > 0 ? 207 : 200;
}

/**
 * Disclaimer copy for a kind, preferring CMS overrides over the core's built-in copy.
 *
 * `overrides` is the `aiImagesConfig` story's content, so legal can reword without a
 * deploy. An absent, blank or whitespace-only override falls through to the core rather
 * than rendering nothing — the disclaimer is the compliance artifact, so an editor clearing
 * a field must not silently remove it from every page.
 */
export function resolveDisclaimer(
  kind: AiMarkerKind,
  overrides?: Partial<Record<AiMarkerKind, string | null>> | null,
  version?: string,
): string {
  const override = overrides?.[kind];

  if (typeof override === "string" && override.trim()) {
    return override;
  }

  return getDisclaimer(kind, version);
}

/** Storyblok's image service only rewrites urls on its own asset hosts. */
const STORYBLOK_ASSET_HOST = /^https:\/\/[a-z0-9-]+\.storyblok\.com\//i;

/**
 * A width-constrained variant of an asset url, for the inventory's preview thumbnails.
 *
 * Loading 632 full-size originals to render them at 48px would make the page unusable, so
 * the thumbnail goes through Storyblok's image service. SVGs are passed through untouched —
 * the service does not rasterise them, and a `/m/` suffix on one returns a 404 rather than
 * a smaller file. Anything not on a Storyblok host is returned as-is.
 */
export function assetThumbUrl(url: string, width = 96): string {
  // A video has no image-service rendition, and appending `/m/` to one returns bytes the
  // browser cannot paint. Callers show the `<video>` itself instead.
  if (!url || !STORYBLOK_ASSET_HOST.test(url)) return url;
  if (/\.svg(\?|$)/i.test(url)) return url;

  return `${url}/m/${width}x0`;
}
